import { useState, useEffect, useRef } from "react";
import { toLocalISOString } from "../../utils/dateUtils";
import { useNavigate } from "react-router-dom";
import Header from "./components/Header";
import CustomerPanel from "./components/CustomerPanel";
// Sales person selection disabled for now — may be revisited in the future.
// import { type SelectedSalesPerson } from "./components/SalesPersonPanel";
import ScanBasketArea from "./components/ScanBasketArea";
import TotalsPanel from "./components/TotalsPanel";
import ActionButtons from "./components/ActionButtons";
import StatusBar from "./components/StatusBar";
import TenderModal from "./components/TenderModal";
import ReturnModal from "./components/ReturnModal";
import SupervisorPinModal from "./components/SupervisorPinModal";
import SupervisorModal from "./components/SupervisorModal";
import PrintReceiptModal from "./components/PrintReceiptModal";
import ParkSaleModal from "./components/ParkSaleModal";
import OrderDiscountModal from "./components/OrderDiscountModal";
import ExchangeModal, {
  type ReturnableInvoice,
} from "./components/ExchangeModal";
import ReturnReceiptModal from "./components/ReturnReceiptModal";
import { posApi } from "../../api/pos";
import { authApi } from "../../api/auth";
import { salesInvoiceApi } from "../../api/salesInvoice";
import { unwrapResponse } from "../../api/client";
import { appendDrawerEvent } from "../../utils/drawerLedger";
import { openSunmiDrawer } from "../../utils/sunmiPrinter";
import { prorateInvoiceDiscount, type InvoiceDiscount } from "../../utils/returnDiscountCalc";
import { toast } from "../../components/toast";
import {
  promotionApi,
  type BogoPromotion,
  type BogoTier,
  type BogoRewardItem,
} from "../../api/promotion";

interface ReturnContext {
  posOrderId: number;
  invoiceId: number;
  customerId: number;
  grandTotal: number;
  // Pre-item-discount gross subtotal across the ORIGINAL invoice's full line set
  // (including any promo/BOGO lines excluded from `lines` below) — the fixed
  // denominator used to prorate `invoiceDiscount` fairly as lines are removed or
  // quantities reduced. Never mix this with a post-item-discount value.
  invoiceGrossSubtotal: number;
  // The original invoice's own order-level discount (type + value), read directly
  // off the invoice detail response — see comment at its computation site for why
  // this must NOT be derived by subtracting item-level discounts from totalDiscountAmount.
  invoiceDiscount: InvoiceDiscount | null;
  lines: Array<{
    itemId: number;
    itemTypeId: number;
    isSerialized: boolean;
    quantity: number;
    price: number;
    discount: number;
    tax: number;
    taxId: number | null;
    amount: number;
    binId: number;
    originalQty: number;
    originalDiscount: number;
    originalTax: number;
    originalAmount: number;
    isPromoItem?: boolean;
  }>;
}

export interface BasketItem {
  line: number;
  code: string;
  name: string;
  qty: number;
  price: number;
  discount: boolean;
  tax: boolean;
  total: number;
  image?: string;
  stock?: number;
  itemId?: number;
  itemTypeId?: number;
  uomId?: number;
  warehouseId?: number;
  isSerialized?: boolean;
  taxes?: any[];
  discounts?: any[];
  brandId?: number | null;
  manufacturerId?: number | null;
  categoryIds?: number[];
  isReturnLine?: boolean;
  originalQty?: number;
  isPromoItem?: boolean;
  isFromParkedSale?: boolean;
  // BOGO metadata — present only on free reward rows, stripped before API submission
  isBogoFreeItem?: boolean;
  bogoPromoId?: number;
  bogoPromoCode?: string;
  bogoPromoName?: string;
  bogoTriggerItemId?: number;
  bogoMinQty?: number;
  bogoIsRecursive?: boolean;
  bogoFreeQtyRatio?: number | null;
  bogoPriceType?: 1 | 2 | 3;
  bogoRewardPrice?: number | null;
  bogoDiscountPct?: number | null;
  bogoOriginalSellingPrice?: number;
  bogoPromoAccountId?: number | null;
  bogoTiers?: BogoTier[] | null;
}

export interface OrderDiscount {
  type: "percentage" | "fixed";
  value: number;
  coupon?: {
    id: number;
    code: string;
    name?: string;
    discountAmount: number;
    discountTypeId: number;
    affiliatorId: number | null;
  };
}

interface SelectedCustomer {
  id: number;
  name: string;
  phoneNumber?: string | null;
  emailAddress?: string | null;
  membershipId?: string | null;
  predefinedValue?: string | null;
}

interface ReceiptSnapshot {
  paymentMethods: Array<{ method: string; amount: number }>;
  actualPayments: Array<{ method: string; amount: number }>;
  basketItems: BasketItem[];
  subtotal: number;
  discount: number;
  orderDiscount: number;
  tax: number;
  total: number;
  appliedCreditMemo?: { amount: number } | null;
  receiptData?: {
    receiptNo?: string;
    transactionId?: string;
    saleDate?: string;
    cashier?: string;
    terminal?: string;
    storeName?: string;
    storeAddress?: string;
    customerName?: string;
    customerCode?: string | null;
    customerMobile?: string | null;
    binNumber?: string | null;
    mushokNumber?: string | null;
    returnPolicy?: string | null;
  };
}

interface ReturnReceiptSnapshot {
  basketItems: BasketItem[];
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
  isRefund: boolean;
  receiptData?: {
    receiptNo?: string;
    creditMemoNumber?: string;
    creditMemoAmount?: number;
    saleDate?: string;
    cashier?: string;
    customerName?: string;
    customerCode?: string | null;
    binNumber?: string | null;
  };
}

const toRateNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const parseDateSafe = (value: unknown): Date | null => {
  if (!value) return null;
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date;
};

const getActiveItemDiscount = (item: BasketItem) => {
  const now = new Date();
  const discounts = Array.isArray(item?.discounts) ? item.discounts : [];
  const active = discounts.find((discount) => {
    const from = parseDateSafe(
      discount?.effectiveFrom ??
        discount?.effectiveFromDate ??
        discount?.startDate ??
        discount?.fromDate ??
        discount?.validFrom,
    );
    const to = parseDateSafe(
      discount?.effectiveTo ??
        discount?.effectiveToDate ??
        discount?.endDate ??
        discount?.toDate ??
        discount?.validTo,
    );
    if (from && now < from) return false;
    if (to && now > to) return false;
    return true;
  });
  return active ?? null;
};

const getItemDiscountAmount = (item: BasketItem): number => {
  const activeDiscount = getActiveItemDiscount(item);
  if (!activeDiscount) return 0;

  const lineBase = Number(item.price || 0) * Number(item.qty || 0);
  if (lineBase <= 0) return 0;

  const typeName = String(
    activeDiscount?.discountType?.name ??
      activeDiscount?.discountTypeName ??
      activeDiscount?.amountType?.name ??
      activeDiscount?.amountTypeName ??
      activeDiscount?.type ??
      activeDiscount?.valueType ??
      "",
  ).toLowerCase();
  const typeId = Number(
    activeDiscount?.discountType?.id ??
      activeDiscount?.discountTypeId ??
      activeDiscount?.amountType?.id ??
      activeDiscount?.amountTypeId ??
      -1,
  );
  const isPercentage =
    activeDiscount?.isPercentage === true ||
    typeId === 1 ||
    typeName.includes("percent");

  const discountValue = toRateNumber(
    activeDiscount?.percentage ??
      activeDiscount?.percent ??
      activeDiscount?.rate ??
      activeDiscount?.discountPercentage ??
      activeDiscount?.amount ??
      activeDiscount?.discountAmount ??
      activeDiscount?.value,
  );
  if (discountValue <= 0) return 0;

  let discountAmount = isPercentage
    ? lineBase * (discountValue / 100)
    : discountValue * Number(item.qty || 0);

  const maxValue = toRateNumber(
    activeDiscount?.maximumValue ??
      activeDiscount?.maximumDiscount ??
      activeDiscount?.maxDiscount ??
      activeDiscount?.maxValue ??
      activeDiscount?.maxAmount ??
      activeDiscount?.maximumAmount,
  );
  if (maxValue > 0) {
    discountAmount = Math.min(discountAmount, maxValue);
  }

  return Math.max(0, Math.min(discountAmount, lineBase));
};

const getItemVatRate = (item: BasketItem): number => {
  const taxes = Array.isArray(item?.taxes) ? item.taxes : [];
  const extractedRate = taxes.reduce((sum, tax) => {
    const rate = toRateNumber(
      tax?.rate ??
        tax?.taxRate ??
        tax?.taxAmount ??
        tax?.percentage ??
        tax?.percent ??
        tax?.vatPercentage ??
        tax?.tax?.rate ??
        tax?.amount,
    );
    return sum + Math.max(0, rate);
  }, 0);
  if (extractedRate > 0) return extractedRate;
  return 0;
};

// ─── BOGO pure helpers ────────────────────────────────────────────────────────

function calcBogoRowAmount(
  priceType: 1 | 2 | 3,
  salePrice: number,
  freeQty: number,
  fixedPrice: number | null,
  discountPct: number | null,
): number {
  switch (priceType) {
    case 2:
      return Math.max(0, (fixedPrice ?? 0) * freeQty);
    case 3:
      return salePrice * freeQty * (1 - (discountPct ?? 0) / 100);
    default:
      return 0;
  }
}

function getApplicableTier(tiers: BogoTier[], quantity: number): BogoTier | null {
  return (
    [...tiers].sort((a, b) => b.buyQty - a.buyQty).find((t) => quantity >= t.buyQty) ?? null
  );
}

function buildBogoFreeRow(
  promo: BogoPromotion,
  rewardItem: BogoRewardItem,
  freeQty: number,
  tiers: BogoTier[] | null,
): Omit<BasketItem, "line"> {
  const amount = calcBogoRowAmount(
    rewardItem.promoPriceTypeId,
    rewardItem.salePrice,
    freeQty,
    rewardItem.fixedPrice,
    rewardItem.discountPercentage,
  );
  return {
    code: "",
    name: rewardItem.name,
    qty: freeQty,
    price: rewardItem.salePrice,
    discount: false,
    tax: false,
    total: amount,
    itemId: rewardItem.id,
    uomId: rewardItem.unitId,
    taxes: [],
    discounts: [],
    isBogoFreeItem: true,
    bogoPromoId: promo.promoId,
    bogoPromoCode: promo.promoCode,
    bogoPromoName: promo.promoName,
    bogoTriggerItemId: promo.triggerItemId,
    bogoMinQty: promo.minQuantity || 1,
    bogoIsRecursive: promo.isRecursive ?? false,
    bogoFreeQtyRatio: rewardItem.qty,
    bogoPriceType: rewardItem.promoPriceTypeId,
    bogoRewardPrice: rewardItem.fixedPrice,
    bogoDiscountPct: rewardItem.discountPercentage,
    bogoOriginalSellingPrice: rewardItem.salePrice,
    bogoPromoAccountId: promo.promoAccountId ?? null,
    bogoTiers: tiers ?? null,
  };
}

// Monetary value of the reward actually given on a BOGO row — not the row's charged
// total. Fully-free (priceType 1) rows have item.total = 0, so the full original price
// is the reward. Fixed-price (2) and percentage-off (3) rows are partly paid, so only
// the discount portion — not the full price — is the reward given to the customer.
export function getBogoRewardValue(item: BasketItem): number {
  const originalPrice = item.bogoOriginalSellingPrice ?? item.price;
  if (item.bogoPriceType === 2 && item.bogoRewardPrice != null) {
    return Math.max(0, originalPrice - item.bogoRewardPrice) * item.qty;
  }
  if (item.bogoPriceType === 3 && item.bogoDiscountPct != null) {
    return Math.max(0, originalPrice * item.qty * (item.bogoDiscountPct / 100));
  }
  return originalPrice * item.qty;
}

function consolidateRewardItems(items: BogoRewardItem[]): BogoRewardItem[] {
  const map = new Map<string, BogoRewardItem>();
  for (const item of items) {
    const key = `${item.id}_${item.promoPriceTypeId}`;
    const existing = map.get(key);
    if (existing) {
      map.set(key, { ...existing, qty: (existing.qty ?? 0) + (item.qty ?? 0) });
    } else {
      map.set(key, { ...item });
    }
  }
  return Array.from(map.values());
}

// Renumber every row 1..N in array order — keeps the displayed Line column
// contiguous regardless of how BOGO rows were inserted/removed.
function renumberLines(items: BasketItem[]): BasketItem[] {
  return items.map((item, idx) => ({ ...item, line: idx + 1 }));
}

// Insert a BOGO free row directly after its trigger item (and after any of the
// trigger's other existing free rows), so the reward stays visually grouped
// with the item that earned it instead of drifting to the end of the basket.
function insertAfterTriggerGroup(
  items: BasketItem[],
  triggerItemId: number,
  newRow: BasketItem,
): BasketItem[] {
  const triggerIdx = items.findIndex((i) => !i.isBogoFreeItem && i.itemId === triggerItemId);
  if (triggerIdx === -1) return [...items, newRow];

  let insertAt = triggerIdx + 1;
  while (
    insertAt < items.length &&
    items[insertAt].isBogoFreeItem &&
    items[insertAt].bogoTriggerItemId === triggerItemId
  ) {
    insertAt++;
  }
  const result = [...items];
  result.splice(insertAt, 0, newRow);
  return result;
}

function applyBogoFreeRows(promos: BogoPromotion[], currentItems: BasketItem[]): BasketItem[] {
  const mainItems = currentItems.filter((i) => !i.isBogoFreeItem);
  const freeRowsByTrigger = new Map<number, Omit<BasketItem, "line">[]>();

  for (const promo of promos) {
    const triggerRow = mainItems.find((i) => i.itemId === promo.triggerItemId);
    if (!triggerRow) continue;
    const triggerQty = triggerRow.qty;
    const rowsForPromo: Omit<BasketItem, "line">[] = [];

    if (promo.tiers && promo.tiers.length > 0) {
      const applicableTier = getApplicableTier(promo.tiers, triggerQty);
      if (applicableTier) {
        const tierIdx = promo.tiers.findIndex((t) => t.level === applicableTier.level);
        const rewardItem = promo.rewardItems[tierIdx] ?? promo.rewardItems[0];
        rowsForPromo.push(buildBogoFreeRow(promo, rewardItem, applicableTier.getQty, promo.tiers));
      }
    } else {
      const consolidated = consolidateRewardItems(promo.rewardItems);
      const minQty = promo.minQuantity || 1;
      for (const rewardItem of consolidated) {
        const rqty = rewardItem.qty ?? 1;
        const freeQty = (promo.isRecursive ?? false)
          ? Math.floor(triggerQty / minQty) * rqty
          : triggerQty >= minQty
            ? rqty
            : 0;
        if (freeQty > 0) {
          rowsForPromo.push(buildBogoFreeRow(promo, rewardItem, freeQty, null));
        }
      }
    }

    if (rowsForPromo.length > 0) {
      const existing = freeRowsByTrigger.get(promo.triggerItemId) ?? [];
      freeRowsByTrigger.set(promo.triggerItemId, [...existing, ...rowsForPromo]);
    }
  }

  const ordered: Omit<BasketItem, "line">[] = [];
  for (const item of mainItems) {
    ordered.push(item);
    const rows = item.itemId != null ? freeRowsByTrigger.get(item.itemId) : undefined;
    if (rows) ordered.push(...rows);
  }
  return renumberLines(ordered.map((item) => ({ ...item, line: 0 })));
}

function patchBogoRowsForTrigger(
  items: BasketItem[],
  promos: BogoPromotion[],
  triggerItemId: number,
  newQty: number,
): BasketItem[] {
  const relevantPromos = promos.filter((p) => p.triggerItemId === triggerItemId);
  if (relevantPromos.length === 0) return items;

  let updated = [...items];

  for (const promo of relevantPromos) {
    if (promo.tiers && promo.tiers.length > 0) {
      const applicableTier = getApplicableTier(promo.tiers, newQty);
      const existingIdx = updated.findIndex(
        (i) =>
          i.isBogoFreeItem &&
          i.bogoTriggerItemId === triggerItemId &&
          i.bogoPromoId === promo.promoId,
      );

      if (!applicableTier) {
        if (existingIdx !== -1) updated.splice(existingIdx, 1);
        continue;
      }

      const tierIdx = promo.tiers.findIndex((t) => t.level === applicableTier.level);
      const rewardItem = promo.rewardItems[tierIdx] ?? promo.rewardItems[0];
      const newFreeQty = applicableTier.getQty;

      if (existingIdx === -1) {
        updated = insertAfterTriggerGroup(updated, triggerItemId, {
          ...buildBogoFreeRow(promo, rewardItem, newFreeQty, promo.tiers),
          line: 0,
        });
      } else {
        updated[existingIdx] = {
          ...buildBogoFreeRow(promo, rewardItem, newFreeQty, promo.tiers),
          line: updated[existingIdx].line,
        };
      }
    } else {
      const minQty = promo.minQuantity || 1;
      const consolidated = consolidateRewardItems(promo.rewardItems);

      for (const rewardItem of consolidated) {
        const rqty = rewardItem.qty ?? 1;
        const newFreeQty = (promo.isRecursive ?? false)
          ? Math.floor(newQty / minQty) * rqty
          : newQty >= minQty
            ? rqty
            : 0;

        const existingIdx = updated.findIndex(
          (i) =>
            i.isBogoFreeItem &&
            i.bogoTriggerItemId === triggerItemId &&
            i.bogoPromoId === promo.promoId &&
            i.itemId === rewardItem.id,
        );

        if (newFreeQty <= 0) {
          if (existingIdx !== -1) updated.splice(existingIdx, 1);
          continue;
        }

        if (existingIdx === -1) {
          updated = insertAfterTriggerGroup(updated, triggerItemId, {
            ...buildBogoFreeRow(promo, rewardItem, newFreeQty, null),
            line: 0,
          });
        } else {
          updated[existingIdx] = {
            ...buildBogoFreeRow(promo, rewardItem, newFreeQty, null),
            line: updated[existingIdx].line,
          };
        }
      }
    }
  }

  return renumberLines(updated);
}

// ─────────────────────────────────────────────────────────────────────────────

export default function POSCheckout() {
  const navigate = useNavigate();

  useEffect(() => {
    authApi
      .checkPermission("PosCashRegisterCheckIn", "StoreTerminal")
      .then((res) => {
        const data = unwrapResponse<any>(res);
        const isAuthorized =
          typeof data === "boolean"
            ? data
            : data?.isAuthorized === true || data?.isAllowed === true;
        if (!isAuthorized) {
          navigate("/pos-checkin", { replace: true });
        }
      })
      .catch(() => {
        navigate("/pos-checkin", { replace: true });
      });
  }, [navigate]);

  useEffect(() => {
    const validateSession = async () => {
      const counterSessionRaw = localStorage.getItem("counterSession");
      if (!counterSessionRaw) {
        navigate("/pos-checkin", { replace: true });
        return;
      }
      let sessionId: number | null = null;
      try {
        const counterSession = JSON.parse(counterSessionRaw);
        if (counterSession?.endTime != null) {
          navigate("/pos-checkin", { replace: true });
          return;
        }
        sessionId =
          counterSession?.id ?? counterSession?.counterSessionId ?? null;
      } catch {
        navigate("/pos-checkin", { replace: true });
        return;
      }
      if (!sessionId) return;
      try {
        const res = await posApi.getCounterSessionInfo(sessionId);
        const session = unwrapResponse<any>(res);
        if (session?.endTime != null) {
          navigate("/pos-checkin", { replace: true });
        }
      } catch {
        // If API fails, don't block — let them proceed
      }
    };
    validateSession();
  }, [navigate]);

  const [mode, setMode] = useState<"sale" | "tender" | "return">("sale");
  const [selectedLineIndex, setSelectedLineIndex] = useState<number | null>(
    null,
  );
  const [showTenderModal, setShowTenderModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showReturnPinModal, setShowReturnPinModal] = useState(false);
  const [showCancelPinModal, setShowCancelPinModal] = useState(false);
  const [showSupervisorModal, setShowSupervisorModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showParkSaleModal, setShowParkSaleModal] = useState(false);
  const [showOrderDiscountModal, setShowOrderDiscountModal] = useState(false);
  const [orderDiscountOutletAccess, setOrderDiscountOutletAccess] = useState<any[]>([]);
  const [orderDiscountAccess, setOrderDiscountAccess] = useState<{
    allowDiscount: boolean;
    applicableDiscount: number;
  } | null>(null);
  const [pendingOrderDiscount, setPendingOrderDiscount] = useState<OrderDiscount | null>(null);
  const [orderDiscountApprovalReason, setOrderDiscountApprovalReason] = useState("");
  const [showOrderDiscountApprovalModal, setShowOrderDiscountApprovalModal] = useState(false);
  const [showExchangeModal, setShowExchangeModal] = useState(false);
  const [returnContext, setReturnContext] = useState<ReturnContext | null>(
    null,
  );
  const [returnProcessed, setReturnProcessed] = useState(false);
  const [isProcessingReturn, setIsProcessingReturn] = useState(false);
  const [returnError, setReturnError] = useState<string | null>(null);
  const [showRefundConfirm, setShowRefundConfirm] = useState(false);
  const [exchangeCreditMemo, setExchangeCreditMemo] = useState<{
    memoId: number;
    amount: number;
  } | null>(null);
  const [parkSaleInitialTab, setParkSaleInitialTab] = useState<
    "park" | "retrieve"
  >("park");
  const [parkSaleAndRetrieve, setParkSaleAndRetrieve] = useState(false);
  const [showRecallConflict, setShowRecallConflict] = useState(false);
  const [hasParkedSales, setHasParkedSales] = useState(
    () => JSON.parse(localStorage.getItem('posParkedSales') ?? '[]').length > 0,
  );
  const [supervisorAction, setSupervisorAction] = useState("");
  const [orderDiscount, setOrderDiscount] = useState<OrderDiscount | null>(
    null,
  );
  const [refundAllowed, setRefundAllowed] = useState(true);
  const [binNumber, setBinNumber] = useState<string | null>(null);
  const [mushokNumber, setMushokNumber] = useState<string | null>(null);
  const [returnPolicy, setReturnPolicy] = useState<string | null>(null);
  const [clearCustomerTrigger, setClearCustomerTrigger] = useState(0);
  // Sales person selection disabled for now — may be revisited in the future.
  // const [clearSalesPersonTrigger, setClearSalesPersonTrigger] = useState(0);
  const [refetchTransactionsTrigger, setRefetchTransactionsTrigger] =
    useState(0);
  const [paymentMethods, setPaymentMethods] = useState<
    Array<{ method: string; amount: number }>
  >([]);
  const [basketItems, setBasketItems] = useState<BasketItem[]>([]);
  const [activeBogoPromos, setActiveBogoPromos] = useState<BogoPromotion[]>([]);
  const activeBogoPromosRef = useRef<BogoPromotion[]>([]);
  const basketItemsRef = useRef<BasketItem[]>([]);
  const [selectedCustomer, setSelectedCustomer] =
    useState<SelectedCustomer | null>(null);
  // Sales person selection disabled for now — may be revisited in the future.
  // const [selectedSalesPerson, setSelectedSalesPerson] =
  //   useState<SelectedSalesPerson | null>(null);
  const [receiptSnapshot, setReceiptSnapshot] =
    useState<ReceiptSnapshot | null>(null);
  const [showReturnReceiptModal, setShowReturnReceiptModal] = useState(false);
  const [returnReceiptSnapshot, setReturnReceiptSnapshot] =
    useState<ReturnReceiptSnapshot | null>(null);

  useEffect(() => {
    posApi
      .getPosSettings()
      .then((res) => {
        const settings = unwrapResponse<any>(res);
        setRefundAllowed(settings?.allowRefund !== false);
        setMushokNumber(settings?.mishukNumber ?? null);
        setReturnPolicy(
          settings?.showReturnPolicy ? settings?.returnPolicy || null : null,
        );
      })
      .catch(() => {
        /* keep default true */
      });
    salesInvoiceApi
      .getInvoiceSettings()
      .then((res) => {
        const settings = unwrapResponse<any>(res);
        setBinNumber(settings?.binNumber ?? null);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (basketItems.length === 0) {
      setOrderDiscount(null);
    }
  }, [basketItems]);

  useEffect(() => {
    activeBogoPromosRef.current = activeBogoPromos;
  }, [activeBogoPromos]);
  useEffect(() => {
    basketItemsRef.current = basketItems;
  }, [basketItems]);

  // True when any reward row's value exceeds its promo's maxRewardsPerOrder
  const bogoRewardCapExceeded = activeBogoPromos.some((promo) => {
    if (promo.maxRewardsPerOrder == null) return false;
    const row = basketItems.find((i) => i.isBogoFreeItem && i.bogoPromoId === promo.promoId);
    return !!row && getBogoRewardValue(row) > promo.maxRewardsPerOrder;
  });

  // True when any reward row's value exceeds its promo's availableBudgetCap
  const bogoBudgetCapExceeded = activeBogoPromos.some((promo) => {
    if (promo.availableBudgetCap == null) return false;
    const row = basketItems.find((i) => i.isBogoFreeItem && i.bogoPromoId === promo.promoId);
    return !!row && getBogoRewardValue(row) > promo.availableBudgetCap;
  });

  const bogoLimitExceeded = bogoRewardCapExceeded || bogoBudgetCapExceeded;

  const prevBogoLimitRef = useRef(false);
  useEffect(() => {
    if (bogoLimitExceeded && !prevBogoLimitRef.current) {
      toast.warn(
        bogoBudgetCapExceeded
          ? "Reward total exceeds the available budget cap. Please reduce or remove the trigger item."
          : "Reward total exceeds the maximum allowed per order. Please reduce or remove the trigger item.",
      );
    }
    prevBogoLimitRef.current = bogoLimitExceeded;
  }, [bogoLimitExceeded, bogoBudgetCapExceeded]);

  // Re-fetch BOGO promos whenever the set of main item IDs or customer changes
  const mainItemIdsKey = basketItems
    .filter((i) => !i.isBogoFreeItem)
    .map((i) => i.itemId)
    .filter(Boolean)
    .sort()
    .join(",");
  const bogoCustomerIdKey = selectedCustomer?.id ?? null;

  useEffect(() => {
    if (returnContext) return;

    const mainItems = basketItems.filter((i) => !i.isBogoFreeItem);
    const itemIds = [...new Set(mainItems.map((i) => i.itemId!).filter(Boolean))];
    const customerId =
      bogoCustomerIdKey != null
        ? Number(bogoCustomerIdKey) > 0
          ? Number(bogoCustomerIdKey)
          : undefined
        : undefined;

    if (itemIds.length === 0) {
      activeBogoPromosRef.current = [];
      setActiveBogoPromos([]);
      setBasketItems((prev) => prev.filter((i) => !i.isBogoFreeItem));
      return;
    }

    const date = new Date().toISOString().split("T")[0];
    const warehouseId = Number(localStorage.getItem("warehouseId") ?? 0) || undefined;
    promotionApi
      .fetchApplicablePromotions({
        itemIds,
        ...(customerId ? { customerId } : {}),
        date,
        isWalkinCustomer: selectedCustomer?.predefinedValue === 'walkincustomer',
        isFromPOS: true,
        ...(warehouseId ? { warehouseId } : {}),
      })
      .then((response) => {
        const raw =
          (response as any)?.data ??
          (response as any)?.Data ??
          response ??
          {};
        const promos: BogoPromotion[] = Object.entries(
          raw as Record<string, any>,
        )
          .filter(([, val]: [string, any]) => val != null)
          .map(([key, val]: [string, any]) => ({
            ...val,
            triggerItemId: +key,
            // API returns this as "minQty", not "minQuantity"
            minQuantity: val.minQty ?? val.minQuantity ?? 1,
          }));
        activeBogoPromosRef.current = promos;
        setActiveBogoPromos(promos);
        setBasketItems(applyBogoFreeRows(promos, basketItemsRef.current));
      })
      .catch((err) => {
        console.error("Error fetching BOGO promotions", err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainItemIdsKey, bogoCustomerIdKey]);

  // Called by ScanBasketArea when a non-BOGO item's qty changes
  const handleBogoQtyChange = (itemId: number, newQty: number) => {
    setBasketItems(current => patchBogoRowsForTrigger(
      current,
      activeBogoPromosRef.current,
      itemId,
      newQty,
    ));
  };

  const toNumericCustomerId = (value: unknown): number | null => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  };
  const resolvedCustomerId = toNumericCustomerId(selectedCustomer?.id);

  // Calculate grand total for tender modal
  const subtotal = basketItems.reduce((sum, item) => sum + item.total, 0);
  const itemDiscounts = basketItems.reduce(
    (sum, item) => sum + getItemDiscountAmount(item),
    0,
  );

  // VAT fixed on subtotal after line item discounts (not affected by order discount)
  const subtotalAfterItemDiscounts = subtotal - itemDiscounts;
  const tax = basketItems
    .filter((item) => getItemVatRate(item) > 0)
    .reduce((sum, item) => {
      const lineDiscount = getItemDiscountAmount(item);
      const itemSubtotal = Math.max(0, item.total - lineDiscount);
      return sum + itemSubtotal * (getItemVatRate(item) / 100);
    }, 0);
  const taxBeforeOrderDiscount = tax;

  // Order discount with coupon/discount interaction rules:
  // Case 1: if manual is 100%, coupon is blocked (handled in modal)
  // Case 2 (fixed): fixed applies first, coupon fills remaining gap
  // Case 3 (percentage): coupon applied first, percentage applies to (subtotal - coupon)
  let orderDiscountAmount = 0;
  let effectiveCouponAmount = 0;
  if (orderDiscount) {
    const rawCoupon = orderDiscount.coupon?.discountAmount ?? 0;
    if (orderDiscount.type === "percentage") {
      const manualBase = Math.max(0, subtotalAfterItemDiscounts - rawCoupon);
      const manualAmount = manualBase * (orderDiscount.value / 100);
      effectiveCouponAmount = rawCoupon;
      orderDiscountAmount = manualAmount + effectiveCouponAmount;
    } else {
      const fixedAmount = Math.min(
        orderDiscount.value,
        subtotalAfterItemDiscounts,
      );
      effectiveCouponAmount = Math.max(
        0,
        Math.min(rawCoupon, subtotalAfterItemDiscounts - fixedAmount),
      );
      orderDiscountAmount = fixedAmount + effectiveCouponAmount;
    }
    orderDiscountAmount = Math.min(
      orderDiscountAmount,
      subtotalAfterItemDiscounts,
    );
  }

  const subtotalAfterDiscounts =
    subtotalAfterItemDiscounts - orderDiscountAmount;
  // Bag fee is temporarily disabled.
  const rawGrandTotal = subtotalAfterDiscounts + tax;
  const grandTotal = Math.round(rawGrandTotal);
  const adjustment = Number((grandTotal - rawGrandTotal).toFixed(2));

  const handleTender = () => {
    if (!basketItems.length) {
      toast.error("Add at least one item before making payment.");
      return;
    }
    if (basketItems.some((item) => !item.itemId)) {
      toast.error(
        "Some items are missing backend ids. Please re-add them from item search.",
      );
      return;
    }
    if (!resolvedCustomerId) {
      toast.error("Select a valid customer before making payment.");
      return;
    }
    // Sales person requirement disabled for now — may be revisited in the future.
    // if (!selectedSalesPerson) {
    //   toast.error("Select a sales person before making payment.");
    //   return;
    // }
    setShowTenderModal(true);
    setMode("tender");
  };

  const handleReturn = () => {
    setShowReturnModal(true);
    setMode("return");
  };

  const handleSupervisorAction = (action: string) => {
    setSupervisorAction(action);
    setShowSupervisorModal(true);
  };

  const handlePrint = () => {
    setShowPrintModal(true);
  };

  const handleCloseTender = () => {
    setShowTenderModal(false);
    setMode("sale");
  };

  const handlePaymentComplete = async (
    methods: Array<{ method: string; amount: number }>,
    payments: any[],
    creditMemo?: { memoId: number; amount: number } | null,
  ) => {
    const parseStorageJson = (raw: string | null) => {
      if (!raw) return {};
      try {
        return JSON.parse(raw);
      } catch {
        return {};
      }
    };

    const pickPositiveNumber = (...values: unknown[]) => {
      for (const value of values) {
        const parsed = Number(value);
        if (Number.isFinite(parsed) && parsed > 0) {
          return parsed;
        }
      }
      return 0;
    };

    const counterRaw = localStorage.getItem("counter");
    const outletRaw = localStorage.getItem("outlet");
    const counterSessionRaw = localStorage.getItem("counterSession");
    const outletSessionRaw = localStorage.getItem("outletSession");
    const outletSessionInfoRaw = localStorage.getItem("outletSessionInfo");
    const warehouseIdRaw = localStorage.getItem("warehouseId");

    let counterId = 0;
    let outletId = 0;
    let counterSessionId = 0;
    let outletSessionId = 0;
    let warehouseId = warehouseIdRaw ? Number(warehouseIdRaw) : 0;
    const counter = parseStorageJson(counterRaw);
    const outlet = parseStorageJson(outletRaw);
    const counterSession = parseStorageJson(counterSessionRaw);
    const outletSession = parseStorageJson(outletSessionRaw);
    const outletSessionInfo = parseStorageJson(outletSessionInfoRaw);

    counterId = pickPositiveNumber(counter?.id, counter?.counterId);
    outletId = pickPositiveNumber(
      outlet?.id,
      outlet?.outletId,
      outletSession?.outlet?.id,
    );
    counterSessionId = pickPositiveNumber(
      counterSession?.id,
      counterSession?.counterSessionId,
      counterSession?.sessionId,
      counterSession?.counterSession?.id,
    );
    outletSessionId = pickPositiveNumber(
      outletSession?.id,
      outletSession?.outletSessionId,
      outletSessionInfo?.id,
      counterSession?.outletSessionId,
      counterSession?.outletSession?.id,
    );

    if (!counterSessionId && counterId) {
      try {
        const lastCounterSessionRes =
          await posApi.getLastCounterSession(counterId);
        const lastCounterSession = unwrapResponse<any>(lastCounterSessionRes);
        counterSessionId = pickPositiveNumber(
          lastCounterSession?.id,
          lastCounterSession?.counterSessionId,
        );
        outletSessionId = pickPositiveNumber(
          outletSessionId,
          lastCounterSession?.outletSession?.id,
          lastCounterSession?.outletSessionId,
        );
      } catch {
        // Keep existing ids and validate below.
      }
    }

    if (!outletSessionId && outletId) {
      try {
        const lastOutletSessionRes =
          await posApi.getLastOutletSession(outletId);
        const lastOutletSession = unwrapResponse<any>(lastOutletSessionRes);
        outletSessionId = pickPositiveNumber(
          lastOutletSession?.id,
          lastOutletSession?.outletSessionId,
        );
      } catch {
        // Keep existing ids and validate below.
      }
    }

    if (!warehouseId) {
      warehouseId =
        pickPositiveNumber(
          outlet?.warehouse?.id,
          outlet?.warehouseId,
          outletSession?.outlet?.warehouse?.id,
          outletSession?.outlet?.warehouseId,
        ) || 0;
    }

    if (!counterId || !counterSessionId || !outletSessionId) {
      throw new Error(
        "Missing counter or session context. Please re-check in and try again.",
      );
    }

    const binId = await fetchBinId();

    let remaining = Math.max(0, grandTotal - (creditMemo?.amount ?? 0));
    const sortedPayments = [...payments].sort((a, b) => {
      const aIsCash = Number(a?.paymentMethodTypeId) === 0 ? 1 : 0;
      const bIsCash = Number(b?.paymentMethodTypeId) === 0 ? 1 : 0;
      return aIsCash - bIsCash;
    });
    const normalizedPayments = sortedPayments.map((payment) => {
      const applied = Math.min(Number(payment?.amount ?? 0), remaining);
      remaining = Math.max(0, remaining - applied);
      return {
        methodId: payment?.methodId ?? null,
        methodName: payment?.methodName ?? payment?.method ?? null,
        accountId: payment?.accountId ?? null,
        amount: applied,
        transactionId: payment?.transactionId ?? null,
        expenseAccountId: payment?.expenseAccountId ?? null,
        charge: Number(payment?.charge ?? 0),
        chargeType: payment?.chargeType ?? null,
        paymentMethodTypeId: payment?.paymentMethodTypeId ?? null,
      };
    });

    const paidAmount = methods.reduce(
      (sum, payment) => sum + Number(payment.amount ?? 0),
      0,
    );
    const totalCovered = paidAmount + (creditMemo?.amount ?? 0);
    const changed = totalCovered > grandTotal ? totalCovered - grandTotal : 0;
    const cashPayment = normalizedPayments.find(
      (payment) => Number(payment?.paymentMethodTypeId) === 0,
    );
    const cashAmount = cashPayment
      ? Math.max(0, Number(cashPayment.amount ?? 0))
      : 0;

    const toInt = (value: unknown) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? Math.trunc(parsed) : 0;
    };

    const orderLines = basketItems.map((item) => {
      // BOGO free rows get a stripped payload with promo fields only
      if (item.isBogoFreeItem) {
        const isFixedPrice = item.bogoPriceType === 2 && item.bogoRewardPrice != null;
        const isPercentageOff = item.bogoPriceType === 3 && item.bogoDiscountPct != null;
        const originalPrice = item.bogoOriginalSellingPrice ?? item.price;
        const fixedDiscountAmount = isFixedPrice
          ? Math.max(0, originalPrice - (item.bogoRewardPrice ?? 0)) * item.qty
          : 0;
        const percentageDiscountAmount = isPercentageOff
          ? Math.max(0, originalPrice * item.qty * ((item.bogoDiscountPct ?? 0) / 100))
          : 0;
        return {
          itemId: toInt(item.itemId),
          itemTypeId: 0,
          quantity: item.qty,
          isSerialized: false,
          uomId: toInt(item.uomId),
          price: item.price,
          taxAmount: 0,
          amount: item.total,
          discountTypeId: isFixedPrice ? 2 : isPercentageOff ? 1 : null,
          discountAmount: isFixedPrice
            ? fixedDiscountAmount
            : isPercentageOff
              ? (item.bogoDiscountPct ?? 0)
              : 0,
          discountItemAmount: isFixedPrice
            ? fixedDiscountAmount
            : isPercentageOff
              ? percentageDiscountAmount
              : 0,
          maximumDiscount: 0,
          subTotal: isFixedPrice || isPercentageOff ? originalPrice * item.qty : item.total,
          itemTransactions: [
            {
              warehouseId: toInt(warehouseId),
              binId: binId || undefined,
              quantity: item.qty,
              itemSerials: [],
              itemBatches: [],
              bogoItem: true,
            },
          ],
          taxes: [],
          promoId: item.bogoPromoId ?? 0,
          promoPriceTypeId: item.bogoPriceType ?? 0,
          triggerItemId: item.bogoTriggerItemId ?? 0,
          promoAccountId: item.bogoPromoAccountId ?? 0,
        };
      }

      const lineDiscountAmount = getItemDiscountAmount(item);
      const activeDiscount = getActiveItemDiscount(item);
      const discountTypeId =
        Number(
          activeDiscount?.discountType?.id ??
            activeDiscount?.discountTypeId ??
            activeDiscount?.amountType?.id ??
            activeDiscount?.amountTypeId ??
            0,
        ) || null;
      const maxDiscount = toRateNumber(
        activeDiscount?.maximumValue ??
          activeDiscount?.maximumDiscount ??
          activeDiscount?.maxDiscount ??
          activeDiscount?.maxValue ??
          activeDiscount?.maxAmount ??
          activeDiscount?.maximumAmount,
      );
      const discountTypeName = String(
        activeDiscount?.discountType?.name ??
          activeDiscount?.discountTypeName ??
          activeDiscount?.amountType?.name ??
          activeDiscount?.amountTypeName ??
          activeDiscount?.type ??
          activeDiscount?.valueType ??
          "",
      ).toLowerCase();
      const isPercentageDiscount =
        activeDiscount?.isPercentage === true ||
        discountTypeId === 1 ||
        discountTypeName.includes("percent");
      const rawDiscountValue = toRateNumber(
        activeDiscount?.percentage ??
          activeDiscount?.percent ??
          activeDiscount?.rate ??
          activeDiscount?.discountPercentage ??
          activeDiscount?.amount ??
          activeDiscount?.discountAmount ??
          activeDiscount?.value,
      );
      // "discount" field must be the rate/percentage value (not monetary), matching server contract
      const discountRateValue =
        lineDiscountAmount > 0
          ? isPercentageDiscount
            ? rawDiscountValue
            : item.total > 0
              ? (lineDiscountAmount / item.total) * 100
              : 0
          : 0;

      const lineTotal = item.total;
      const discountedLineTotal = lineTotal - lineDiscountAmount;
      const lineVatRate = getItemVatRate(item);

      return {
        itemId: toInt(item.itemId),
        itemTypeId: 0,
        quantity: item.qty,
        isSerialized: !!item.isSerialized,
        uomId: toInt(item.uomId),
        price: item.price,
        taxAmount: discountedLineTotal * (lineVatRate / 100),
        amount: discountedLineTotal,
        discountTypeId,
        discountAmount: discountRateValue,
        discountItemAmount: lineDiscountAmount,
        maximumDiscount: maxDiscount,
        subTotal: lineTotal,
        itemTransactions: [
          {
            warehouseId: toInt(warehouseId),
            binId: binId || undefined,
            quantity: item.qty,
            itemSerials: [],
            itemBatches: [],
          },
        ],
        taxes: (item.taxes ?? []).map((t: any) => ({ taxId: t.taxId ?? t.id })),
      };
    });

    const payload = {
      saleDate: toLocalISOString(),
      customerId: resolvedCustomerId,
      counterId,
      outletId: outletId || null,
      currencyId: 1,
      totalTaxAmount: tax,
      totalDiscountAmount: itemDiscounts + orderDiscountAmount,
      subTotal: subtotal,
      grandTotal,
      adjustment,
      discount: orderDiscount
        ? orderDiscount.coupon
          ? orderDiscountAmount > 0
            ? orderDiscountAmount
            : null
          : orderDiscount.value > 0
            ? orderDiscount.value
            : null
        : null,
      discountTypeId: orderDiscount
        ? orderDiscount.coupon
          ? 2
          : orderDiscount.type === "percentage"
            ? 1
            : 2
        : null,
      ...(orderDiscount?.coupon
        ? {
            couponId: orderDiscount.coupon.id,
            couponDiscountType: orderDiscount.coupon.discountTypeId,
            couponDiscountValue: effectiveCouponAmount,
            couponAffiliatorId: orderDiscount.coupon.affiliatorId,
          }
        : {}),
      orderLines,
      cashAmount,
      returnAmount: creditMemo?.amount ?? 0,
      counterSessionId,
      outletSessionId,
      totalAmountWithoutDiscount: subtotal,
      payments: normalizedPayments,
      changed,
      actualPaymentsInfo: normalizedPayments,
      applyCreditMemo: creditMemo
        ? { memoId: creditMemo.memoId, amount: creditMemo.amount }
        : null,
      // Sales person selection disabled for now — may be revisited in the future.
      // salesPersonId: selectedSalesPerson?.id ?? null,
      salesPersonId: null,
    };

    const response = await posApi.createPosOrder(payload);
    const orderResult = unwrapResponse<any>(response);
    if (!orderResult) {
      throw new Error("Failed to create sale order.");
    }

    setPaymentMethods(methods);
    setReceiptSnapshot({
      paymentMethods: methods,
      actualPayments: normalizedPayments
        .filter((payment) => payment.amount > 0)
        .map((payment) => ({
          method: payment.methodName ?? "",
          amount: payment.amount,
        })),
      basketItems: [...basketItems],
      subtotal,
      discount: itemDiscounts + orderDiscountAmount,
      orderDiscount: orderDiscountAmount,
      tax,
      total: grandTotal,
      appliedCreditMemo: creditMemo ? { amount: creditMemo.amount } : null,
      receiptData: {
        receiptNo:
          orderResult?.receiptNo ??
          orderResult?.invoiceNumber ??
          orderResult?.orderNumber ??
          orderResult?.invoiceNo ??
          null,
        transactionId:
          orderResult?.transactionId ??
          orderResult?.id ??
          orderResult?.orderId ??
          null,
        saleDate: toLocalISOString(),
        cashier:
          counterSession?.user?.name ??
          counterSession?.cashierName ??
          JSON.parse(localStorage.getItem("userInfo") || "{}")?.name ??
          "",
        terminal:
          JSON.parse(localStorage.getItem("counterCheckedInInfo") || "{}")
            ?.counterName ?? "",
        storeName:
          JSON.parse(localStorage.getItem("counterCheckedInInfo") || "{}")
            ?.outletName ?? "",
        storeAddress: outlet?.address ?? outlet?.location ?? "",
        customerName: selectedCustomer?.name ?? "Walk In Customer",
        customerCode: selectedCustomer?.membershipId ?? null,
        customerMobile: selectedCustomer?.phoneNumber ?? null,
        binNumber: binNumber,
        mushokNumber: mushokNumber,
        returnPolicy: returnPolicy,
      },
    });

    const completedSales = JSON.parse(
      sessionStorage.getItem("completedSales") || "[]",
    );
    completedSales.push({
      id:
        orderResult?.id ??
        orderResult?.orderId ??
        orderResult?.transactionId ??
        Date.now(),
      timestamp: toLocalISOString(),
      total: grandTotal,
      paymentMethods: methods,
    });
    sessionStorage.setItem("completedSales", JSON.stringify(completedSales));

    if (cashAmount > 0) {
      openSunmiDrawer();
      appendDrawerEvent({
        type: grandTotal >= 0 ? "cash_sale" : "cash_refund",
        amount: Math.abs(cashAmount),
        transactionId: String(
          orderResult?.receiptNo ??
            orderResult?.invoiceNumber ??
            orderResult?.orderNumber ??
            orderResult?.id ??
            "",
        ),
        user:
          counterSession?.user?.name ??
          JSON.parse(localStorage.getItem("userInfo") || "{}")?.name ??
          "Cashier",
        role: "Cashier",
        note:
          grandTotal >= 0
            ? "Cash received from sale"
            : "Cash refunded to customer",
      });
    }

    setShowPrintModal(true);
    setBasketItems([]);
    setSelectedLineIndex(null);
    setOrderDiscount(null);
    setSelectedCustomer(null);
    // setSelectedSalesPerson(null);
    setClearCustomerTrigger((prev) => prev + 1);
    // setClearSalesPersonTrigger((prev) => prev + 1);
    setRefetchTransactionsTrigger((prev) => prev + 1);
    setMode("sale");
    setReturnContext(null);
    setReturnProcessed(false);
    setReturnError(null);
    setExchangeCreditMemo(null);
  };

  const handleCloseReturn = () => {
    setShowReturnModal(false);
    setMode("sale");
  };

  const handleCloseSupervisor = () => {
    setShowSupervisorModal(false);
    setSupervisorAction("");
  };

  const handleClosePrint = () => {
    setShowPrintModal(false);
    setReceiptSnapshot(null);
  };

  const handleCloseReturnReceipt = () => {
    // For exchange receipts: returnContext is no longer needed after the credit memo
    // is created. Clearing it re-enables the Exchange button so the user can start
    // another return or add exchange items normally.
    if (returnReceiptSnapshot && !returnReceiptSnapshot.isRefund) {
      setReturnContext(null);
      setReturnProcessed(false);
    }
    setIsProcessingReturn(false); // safety net in case it got stuck
    setShowReturnReceiptModal(false);
    setReturnReceiptSnapshot(null);
  };

  const handleParkSale = () => {
    setParkSaleInitialTab("park");
    setShowParkSaleModal(true);
  };

  const handleCloseParkSale = () => {
    setShowParkSaleModal(false);
    setHasParkedSales(JSON.parse(localStorage.getItem('posParkedSales') ?? '[]').length > 0);
  };

  const handleParkSuccess = () => {
    // Clear the basket after parking
    setBasketItems([]);
    setSelectedLineIndex(null);
  };

  const handleRetrieveSale = (
    items: BasketItem[],
    customer?: SelectedCustomer | null,
  ) => {
    // Load the retrieved items into the basket, marking them as recalled
    setBasketItems(items.map(i => ({ ...i, isFromParkedSale: true })));
    if (customer !== undefined) {
      setSelectedCustomer(customer);
    }
    setSelectedLineIndex(null);
  };

  const handleCustomerSelect = (customer: SelectedCustomer | null) => {
    // Cart items are kept across a customer change — the BOGO effect
    // (keyed on bogoCustomerIdKey) re-fetches and re-applies promotions
    // for the new customer against the existing basket.
    setSelectedCustomer(customer);
  };

  const recallSingleParkedSale = (): boolean => {
    const parked = JSON.parse(localStorage.getItem('posParkedSales') ?? '[]');
    if (!Array.isArray(parked) || parked.length !== 1) return false;
    const sale = parked[0];
    localStorage.setItem('posParkedSales', '[]');
    setHasParkedSales(false);
    handleRetrieveSale(sale.items, sale.customer);
    return true;
  };

  const handleRecall = () => {
    if (basketItems.length > 0) {
      setShowRecallConflict(true);
      return;
    }
    if (recallSingleParkedSale()) return;
    setParkSaleInitialTab("retrieve");
    setParkSaleAndRetrieve(false);
    setShowParkSaleModal(true);
  };

  const handleConflictParkAndRetrieve = () => {
    setShowRecallConflict(false);
    setParkSaleInitialTab("park");
    setParkSaleAndRetrieve(true);
    setShowParkSaleModal(true);
  };

  const handleConflictClearAndRetrieve = () => {
    setShowRecallConflict(false);
    setBasketItems([]);
    setSelectedLineIndex(null);
    if (recallSingleParkedSale()) return;
    setParkSaleInitialTab("retrieve");
    setParkSaleAndRetrieve(false);
    setShowParkSaleModal(true);
  };

  const handleOrderDiscount = async () => {
    if (!basketItems.length) {
      toast.error("Add at least one item before applying a discount.");
      return;
    }
    let access = { allowDiscount: false, applicableDiscount: 0 };
    try {
      const outletRaw = localStorage.getItem("outlet");
      const outletId = outletRaw ? Number(JSON.parse(outletRaw)?.id ?? 0) : 0;
      const userId = Number(localStorage.getItem("userId") ?? "");
      const res = await posApi.getOutletAccess({ outletId, userId });
      const list = unwrapResponse<any[]>(res);
      const outletAccess = Array.isArray(list) ? list : [];
      setOrderDiscountOutletAccess(outletAccess);
      const userAccess = outletAccess.find(
        (entry) =>
          Number(entry?.outlet?.id) === outletId &&
          Number(entry?.user?.id) === userId,
      );
      access = userAccess
        ? {
            allowDiscount: userAccess.allowDiscount === true,
            applicableDiscount: Number(userAccess.applicableDiscount ?? 0),
          }
        : { allowDiscount: false, applicableDiscount: 0 };
      setOrderDiscountAccess(access);
    } catch {
      setOrderDiscountOutletAccess([]);
      setOrderDiscountAccess(access);
    }

    if (!access.allowDiscount) {
      // No permission at all — ask for supervisor approval before ever showing the discount modal.
      setPendingOrderDiscount(null);
      setOrderDiscountApprovalReason(
        "You do not have permission to give a discount. Enter approval password to continue.",
      );
      setShowOrderDiscountApprovalModal(true);
      return;
    }

    setShowOrderDiscountModal(true);
  };

  const handleCloseOrderDiscount = () => {
    setShowOrderDiscountModal(false);
  };

  const handleApplyOrderDiscount = (discount: OrderDiscount) => {
    setOrderDiscount(discount);
  };

  const handleRequireOrderDiscountApproval = (
    discount: OrderDiscount,
    reason: string,
  ) => {
    setPendingOrderDiscount(discount);
    setOrderDiscountApprovalReason(reason);
    setShowOrderDiscountApprovalModal(true);
  };

  const handleRemoveOrderDiscount = () => {
    setOrderDiscount(null);
  };

  const handleReturnQtyUpdate = (itemId: number, qty: number) => {
    setReturnContext((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        lines: prev.lines.map((l) => {
          if (l.itemId !== itemId) return l;
          const ratio = l.originalQty > 0 ? qty / l.originalQty : 1;
          return {
            ...l,
            quantity: qty,
            discount: l.originalDiscount * ratio,
            tax: l.originalTax * ratio,
            amount: l.originalAmount * ratio,
          };
        }),
      };
    });
  };

  const handleReturnLineRemove = (itemId: number) => {
    setReturnContext((prev) => {
      if (!prev) return prev;
      return { ...prev, lines: prev.lines.filter((l) => l.itemId !== itemId) };
    });
  };

  // Reprorates the header discount remainder onto only the lines/quantities
  // still marked for return, every time returnContext.lines changes (qty
  // edited or a line removed). Both sides of the ratio (returnGrossSubtotal,
  // invoiceGrossSubtotal) are pre-item-discount — see prorateInvoiceDiscount.
  useEffect(() => {
    if (!returnContext) return;
    if (!returnContext.invoiceDiscount) {
      setOrderDiscount(null);
      return;
    }
    const returnGrossSubtotal = returnContext.lines.reduce(
      (sum, l) => sum + l.price * l.quantity,
      0,
    );
    const returnNetSubtotal = returnContext.lines.reduce(
      (sum, l) => sum + (l.price * l.quantity - l.discount),
      0,
    );
    const prorated = prorateInvoiceDiscount(
      returnContext.invoiceDiscount,
      { returnGrossSubtotal, returnNetSubtotal },
      returnContext.invoiceGrossSubtotal,
    );
    setOrderDiscount(prorated > 0 ? { type: "fixed", value: prorated } : null);
  }, [returnContext]);

  const doCancelTransaction = () => {
    setBasketItems([]);
    setSelectedLineIndex(null);
    setOrderDiscount(null);
    setMode("sale");
    setSelectedCustomer(null);
    // setSelectedSalesPerson(null);
    setReturnContext(null);
    setReturnProcessed(false);
    setReturnError(null);
    setExchangeCreditMemo(null);
    setClearCustomerTrigger((prev) => prev + 1);
    // setClearSalesPersonTrigger((prev) => prev + 1);
  };

  const handleCancelTransaction = () => {
    if (basketItems.some((i) => i.isFromParkedSale)) {
      setShowCancelPinModal(true);
      return;
    }
    doCancelTransaction();
  };

  const handleReturnInvoiceSelected = async (invoice: ReturnableInvoice) => {
    const customerId = Number(
      invoice?.partyProfile?.id ??
        invoice?.customer?.id ??
        invoice?.customerId ??
        0,
    );
    const posOrderId = Number(invoice?.posOrderId ?? invoice.id);
    const invoiceGrandTotal = Number(invoice.grandTotal ?? 0);

    // Try to get lines from invoice response first, otherwise fetch POS order details
    let rawLines: any[] = Array.isArray(invoice.lines)
      ? invoice.lines
      : Array.isArray((invoice as any).posOrderLines)
        ? (invoice as any).posOrderLines
        : Array.isArray((invoice as any).orderLines)
          ? (invoice as any).orderLines
          : [];

    // Always fetch full invoice detail — it has memberShipId on partyProfile and the real POS order ID
    const invoiceRes = await salesInvoiceApi.getInvoiceById(invoice.id);
    const fullInvoiceDetail: any = unwrapResponse<any>(invoiceRes);

    const actualPosOrderId = Number(
      fullInvoiceDetail?.referenceId?.id ??
        fullInvoiceDetail?.posOrderId ??
        fullInvoiceDetail?.posOrder?.id ??
        posOrderId,
    );

    if (!rawLines.length) {
      rawLines = Array.isArray(fullInvoiceDetail?.invoiceItemList)
        ? fullInvoiceDetail.invoiceItemList
        : Array.isArray(fullInvoiceDetail?.orderLines)
          ? fullInvoiceDetail.orderLines
          : Array.isArray(fullInvoiceDetail?.posOrderLines)
            ? fullInvoiceDetail.posOrderLines
            : Array.isArray(fullInvoiceDetail?.lines)
              ? fullInvoiceDetail.lines
              : [];
    }

    // Full original invoice gross subtotal (pre-item-discount), including
    // promo/BOGO lines that are excluded below — this is the fixed denominator
    // for prorating the header discount remainder, and must stay pre-discount
    // on both sides of that ratio (see prorateInvoiceDiscount).
    const invoiceGrossSubtotal = rawLines.reduce((sum: number, l: any) => {
      const qty = Number(l?.quantity ?? l?.qty ?? 1);
      const price = Number(l?.price ?? l?.unitPrice ?? l?.sellingPrice ?? 0);
      return sum + qty * price;
    }, 0);

    const isNonReturnable = (l: any) =>
      l.isPromoItem ||
      l.promoId > 0 ||
      !!(l.itemTransactions?.[0]?.bogoItem);

    const excludedCount = rawLines.filter(isNonReturnable).length;
    rawLines = rawLines.filter((l: any) => !isNonReturnable(l));
    if (rawLines.length === 0) {
      toast.error("This invoice contains only promotional items which cannot be returned, refunded, or exchanged.");
      throw new Error("");
    }
    if (excludedCount > 0) {
      toast.warn(`${excludedCount} promotional item${excludedCount > 1 ? 's' : ''} excluded — promo items cannot be returned.`);
    }

    const lines = rawLines.map((l: any) => {
      const qty = Number(l?.quantity ?? l?.qty ?? 1);
      const price = Number(l?.price ?? l?.unitPrice ?? l?.sellingPrice ?? 0);
      // totalItemDiscount / discount / discountAmount on this invoice-detail response
      // are all rate-like (percentage-equivalent) fields, NEVER a dollar amount —
      // confirmed against a real "Per Item" line where itemDiscount[0].amount=60 (per
      // unit) but totalItemDiscount=10 (its %-equivalent on a 1200 gross). The only
      // reliable dollar figure is the itemDiscount/discounts array, computed the exact
      // same way as a live sale (getItemDiscountAmount) so return and sale always agree.
      const discList = Array.isArray(l?.itemDiscount)
        ? l.itemDiscount
        : Array.isArray(l?.discounts)
          ? l.discounts
          : [];
      const discount = getItemDiscountAmount({ price, qty, discounts: discList } as BasketItem);
      const lineTax = Number(l?.totalTaxAmount ?? l?.taxAmount ?? l?.tax ?? 0);
      const lineAmount = Number(l?.total ?? Math.max(0, price * qty - discount));
      return ({
      itemId: Number(l?.item?.id ?? l?.itemId ?? 0),
      itemTypeId: Number(l?.itemType?.id ?? l?.itemTypeId ?? 0),
      isSerialized: !!(l?.isSerialized ?? l?.serialized),
      quantity: qty,
      price,
      discount,
      tax: lineTax,
      taxId: l?.taxId ?? l?.taxes?.[0]?.taxId ?? null,
      amount: lineAmount,
      binId: Number(
        l?.itemTransactions?.[0]?.binId ??
          l?.bin?.id ??
          l?.binId ??
          l?.warehouseId ??
          0,
      ),
      originalQty: qty,
      originalDiscount: discount,
      originalTax: lineTax,
      originalAmount: lineAmount,
      isPromoItem: !!(l?.isPromoItem),
    });
    });

    // The original invoice reports its own order-level discount directly — a rate
    // (Percentage) or a dollar total (Fixed/otherwise) — via discountType/discount/
    // totalDiscountAmount. Read those directly rather than inferring a "remainder" by
    // subtracting item-level discounts from totalDiscountAmount: confirmed against a
    // real invoice where subTotal(1260) * discount(10%) = totalDiscountAmount(126)
    // exactly, i.e. totalDiscountAmount is ALREADY order-level-only — it never included
    // the item-level discount, so subtracting it would double-discount the remainder.
    const invoiceDiscountTypeId = Number(
      fullInvoiceDetail?.discountType?.id ?? (invoice as any)?.discountType?.id ?? -1,
    );
    const invoiceDiscountTypeName = String(
      fullInvoiceDetail?.discountType?.name ?? (invoice as any)?.discountType?.name ?? "",
    ).toLowerCase();
    const isInvoicePercentageDiscount =
      invoiceDiscountTypeId === 1 || invoiceDiscountTypeName.includes("percent");
    const invoiceDiscountRate = Number(
      fullInvoiceDetail?.discount ?? (invoice as any)?.discount ?? 0,
    );
    const invoiceDiscountAmount = Number(
      fullInvoiceDetail?.totalDiscountAmount ?? (invoice as any)?.totalDiscountAmount ?? 0,
    );
    const invoiceDiscount: InvoiceDiscount | null =
      isInvoicePercentageDiscount && invoiceDiscountRate > 0
        ? { kind: "Percentage", percent: invoiceDiscountRate }
        : invoiceDiscountAmount > 0
          ? { kind: "Fixed", amount: invoiceDiscountAmount }
          : null;

    const grandTotal = invoiceGrandTotal || lines.reduce((s, l) => s + l.amount, 0);

    setReturnContext({
      posOrderId: actualPosOrderId,
      invoiceId: Number(invoice.id ?? 0),
      customerId,
      grandTotal,
      invoiceGrossSubtotal,
      invoiceDiscount,
      lines,
    });
    setReturnProcessed(false);
    setReturnError(null);

    // Attach customer from invoice partyProfile — prefer fullInvoiceDetail (has memberShipId) over the partial list item
    const src = fullInvoiceDetail ?? invoice;
    const invoiceCustomerId = Number(src?.partyProfile?.id ?? src?.customer?.id ?? src?.customerId ?? 0);
    const invoiceCustomerName = src?.partyProfile?.name ?? src?.customer?.name ?? src?.customerName ?? "";
    if (invoiceCustomerId > 0 && invoiceCustomerName) {
      setSelectedCustomer({
        id: invoiceCustomerId,
        name: invoiceCustomerName,
        phoneNumber: src?.partyProfile?.phoneNumber ?? null,
        emailAddress: src?.partyProfile?.emailAddress ?? null,
        membershipId: src?.partyProfile?.memberShipId ?? src?.partyProfile?.membershipId ?? src?.customer?.membershipId ?? src?.membershipId ?? null,
      });
    }

    // Sales person selection disabled for now — may be revisited in the future.
    // const invoiceSalesPersonId = Number(invoice?.salesPerson?.id ?? 0);
    // const invoiceSalesPersonName = invoice?.salesPerson?.name ?? "";
    // if (invoiceSalesPersonName) {
    //   setSelectedSalesPerson({ id: invoiceSalesPersonId, name: invoiceSalesPersonName });
    // }

    // Patch invoice items into the basket as return lines (negative qty/total)
    const returnBasketItems: BasketItem[] = rawLines.map(
      (l: any, idx: number) => {
        const qty = Number(l?.quantity ?? l?.qty ?? 1);
        const price = Number(l?.price ?? l?.unitPrice ?? l?.sellingPrice ?? 0);
        return {
          line: idx + 1,
          code:
            l?.itemCode ??
            l?.code ??
            l?.item?.code ??
            l?.item?.itemCode ??
            String(l?.item?.id ?? l?.itemId ?? ""),
          name:
            l?.item?.name ??
            l?.itemName ??
            l?.name ??
            l?.item?.itemName ??
            "Returned Item",
          qty: Math.abs(qty),
          isReturnLine: true,
          originalQty: Math.abs(qty),
          price,
          discount:
            (l?.totalItemDiscount ?? l?.discountAmount ?? l?.discount ?? 0) > 0,
          tax: (l?.totalTaxAmount ?? l?.taxAmount ?? l?.tax ?? 0) > 0,
          total: price * qty,
          itemId: Number(l?.item?.id ?? l?.itemId ?? 0) || undefined,
          itemTypeId:
            Number(l?.itemType?.id ?? l?.itemTypeId ?? 0) || undefined,
          isSerialized: !!(l?.isSerialized ?? l?.serialized),
          isPromoItem: !!(l?.isPromoItem),
          taxes: (Array.isArray(l?.taxes) ? l.taxes : []).map((t: any) => ({
            ...t,
            rate: Number(t?.taxAmount ?? t?.taxRate ?? t?.rate ?? t?.percentage ?? t?.percent ?? 0),
          })),
          discounts: Array.isArray(l?.itemDiscount)
            ? l.itemDiscount
            : Array.isArray(l?.discounts)
              ? l.discounts
              : [],
        };
      },
    );

    setBasketItems(returnBasketItems);
  };

  const getReturnApiBase = () => {
    const counterSessionRaw = localStorage.getItem("counterSession") ?? "{}";
    const counterRaw = localStorage.getItem("counter") ?? "{}";
    let session: any = {};
    let counter: any = {};
    try {
      session = JSON.parse(counterSessionRaw);
    } catch {
      /* */
    }
    try {
      counter = JSON.parse(counterRaw);
    } catch {
      /* */
    }
    const counterId = Number(
      counter?.id ??
        counter?.counterId ??
        session?.counter?.id ??
        session?.counterId ??
        0,
    );
    const counterSessionId = Number(
      session?.id ??
        session?.counterSessionId ??
        session?.sessionId ??
        session?.counterSession?.id ??
        0,
    );
    return { counterId, counterSessionId };
  };

  const fetchBinId = async (): Promise<number> => {
    const warehouseId = Number(localStorage.getItem("warehouseId") ?? 0);
    if (!warehouseId) return 0;
    try {
      const warehouseRes = await posApi.getWarehouseById(warehouseId);
      const warehouseData = unwrapResponse<any>(warehouseRes);
      const binLocations = Array.isArray(warehouseData?.binLocations)
        ? [...warehouseData.binLocations].sort((a, b) => a.position - b.position)
        : [];
      return Number(binLocations[0]?.id ?? 0);
    } catch (e) {
      console.error("getWarehouseById failed:", e);
      return 0;
    }
  };

  const buildReturnPayload = (
    rc: ReturnContext,
    paymentMethodId: number | null,
    paymentAccountId: number | null,
    createRefund: boolean,
    binId: number,
    basketSubTotal: number,
    basketDiscountAmount: number,
    basketTax: number,
    basketGrandTotal: number,
  ) => {
    const { counterId, counterSessionId } = getReturnApiBase();
    // Mirror the sale-creation payload's convention exactly: subTotal is gross, and
    // totalDiscountAmount alone carries the discount (item-level + order-level). Each
    // line's own discount/amount stays genuine (0 when the original discount was
    // order-level) — the backend posts an independent "discount given" ledger entry for
    // both per-line discount and totalDiscountAmount, so prorating a share into every
    // line as well would double-count it in accounting.
    const returnableLines = rc.lines.filter((l) => !l.isPromoItem);
    return {
      counterId,
      counterSessionId,
      paymentMethod: paymentMethodId ?? 0,
      paymentAccountId: paymentAccountId ?? 0,
      subTotal: basketSubTotal,
      totalTaxAmount: basketTax,
      grandTotal: basketGrandTotal,
      refundAmount: createRefund ? basketGrandTotal : null,
      totalDiscountAmount: basketDiscountAmount,
      reason: "",
      date: toLocalISOString(),
      createRefund,
      customerId: rc.customerId,
      posOrderId: rc.posOrderId,
      invoiceId: rc.invoiceId,
      posReturnLines: returnableLines.map((l) => ({
        itemId: l.itemId,
        itemTypeId: l.itemTypeId,
        isSerialized: l.isSerialized,
        quantity: l.quantity,
        price: l.price,
        discount: l.discount,
        tax: l.tax,
        taxId: l.taxId ?? 0,
        amount: Math.max(0, l.price * l.quantity - l.discount),
        itemTransactions: [
          {
            binId,
            quantity: l.quantity,
            itemSerials: [],
            itemBatches: [],
          },
        ],
      })),
    };
  };

  const handleRefundClick = () => {
    if (!returnContext) return;
    setShowRefundConfirm(true);
  };

  const handleConfirmRefund = async () => {
    if (!returnContext) return;
    setIsProcessingReturn(true);
    setReturnError(null);
    try {
      let cashMethodId: number | null = null;
      let cashAccountId: number | null = null;
      try {
        const outletRaw = localStorage.getItem("outlet");
        const outletId = outletRaw ? Number(JSON.parse(outletRaw)?.id ?? 0) : 0;
        if (outletId) {
          const outletRes = await posApi.getOutletById(outletId);
          const outletDetails = unwrapResponse<any>(outletRes);
          if (outletDetails?.cashMethodId) {
            cashMethodId = Number(outletDetails.cashMethodId);
            cashAccountId = Number(outletDetails?.cashAccountId ?? 0) || null;
          }
        }
      } catch {
        /* fall back to null */
      }
      const binId = await fetchBinId();
      const payload = buildReturnPayload(
        returnContext,
        cashMethodId,
        cashAccountId,
        true,
        binId,
        subtotal,
        itemDiscounts + orderDiscountAmount,
        tax,
        grandTotal,
      );
      const res = await posApi.createPosReturn(payload);
      const result = unwrapResponse<any>(res);
      const succeeded = (res as any)?.succeeded ?? result?.succeeded ?? true;
      const msg =
        (res as any)?.message ?? result?.message ?? "Refund processed successfully.";
      if (!succeeded) {
        setReturnError(msg);
        toast.error(msg);
        return;
      }
      toast.success(msg);

      const _userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
      const refundSnapshot: ReturnReceiptSnapshot = {
        basketItems: [...basketItems],
        subtotal,
        discount: itemDiscounts,
        tax,
        total: grandTotal,
        isRefund: true,
        receiptData: {
          receiptNo:
            result?.number ??
            result?.returnNo ??
            result?.returnId ??
            String(result?.creditMemoId ?? result?.memoId ?? result?.id ?? returnContext.invoiceId),
          creditMemoNumber: result?.creditMemoNumber ?? "",
          saleDate: new Date().toISOString(),
          cashier: _userInfo?.name ?? _userInfo?.email ?? "",
          customerName: selectedCustomer?.name ?? "Walk In Customer",
          customerCode: selectedCustomer?.membershipId ?? null,
          binNumber: binNumber,
        },
      };
      setReturnReceiptSnapshot(refundSnapshot);
      setShowReturnReceiptModal(true);

      setReturnContext(null);
      setReturnProcessed(false);
      setExchangeCreditMemo(null);
      setShowRefundConfirm(false);
      setBasketItems([]);
      setSelectedLineIndex(null);
      setOrderDiscount(null);
      setSelectedCustomer(null);
      // setSelectedSalesPerson(null);
      setMode("sale");
      setClearCustomerTrigger((prev) => prev + 1);
      // setClearSalesPersonTrigger((prev) => prev + 1);
    } catch (err: any) {
      const msg = err?.message || "Failed to process refund.";
      setReturnError(msg);
      toast.error(msg);
    } finally {
      setIsProcessingReturn(false);
    }
  };

  const handleExchangeAction = async () => {
    if (!returnContext) return;
    setIsProcessingReturn(true);
    setReturnError(null);
    try {
      const binId = await fetchBinId();
      const payload = buildReturnPayload(returnContext, null, null, false, binId, subtotal, itemDiscounts + orderDiscountAmount, tax, grandTotal);
      const returnRes = await posApi.createPosReturn(payload);
      const returnResult = unwrapResponse<any>(returnRes);
      const exchangeSucceeded =
        (returnRes as any)?.succeeded ?? returnResult?.succeeded ?? true;
      const exchangeMsg =
        (returnRes as any)?.message ??
        returnResult?.message ??
        "Exchange return processed. Add new items and proceed to payment.";
      if (!exchangeSucceeded) {
        setReturnError(exchangeMsg);
        toast.error(exchangeMsg);
        return;
      }
      const memoId = Number(
        returnResult?.creditMemoId ??
          returnResult?.memoId ??
          returnResult?.id ??
          0,
      );
      if (memoId) {
        setExchangeCreditMemo({ memoId, amount: returnContext.grandTotal });
      }

      const _userInfoEx = JSON.parse(localStorage.getItem("userInfo") || "{}");
      const exchangeSnapshot: ReturnReceiptSnapshot = {
        basketItems: [...basketItems],
        subtotal,
        discount: itemDiscounts,
        tax,
        total: grandTotal,
        isRefund: false,
        receiptData: {
          receiptNo:
            returnResult?.number ??
            returnResult?.returnNo ??
            returnResult?.returnId ??
            String(memoId || returnContext.invoiceId),
          creditMemoNumber: returnResult?.creditMemoNumber ?? "",
          creditMemoAmount: returnContext.grandTotal,
          saleDate: new Date().toISOString(),
          cashier: _userInfoEx?.name ?? _userInfoEx?.email ?? "",
          customerName: selectedCustomer?.name ?? "Walk In Customer",
          customerCode: selectedCustomer?.membershipId ?? null,
          binNumber: binNumber,
        },
      };
      setReturnReceiptSnapshot(exchangeSnapshot);
      setShowReturnReceiptModal(true);

      setReturnProcessed(true);
      setBasketItems([]);
      setSelectedLineIndex(null);
      // Clear the order-level discount synthesized from the returned invoice — it was
      // only needed to compute this return's payload; leaving it set would incorrectly
      // discount whatever new item the cashier adds next as the exchange replacement.
      setOrderDiscount(null);
      toast.success(exchangeMsg);
    } catch (err: any) {
      const msg = err?.message || "Failed to process exchange return.";
      setReturnError(msg);
      toast.error(msg);
    } finally {
      setIsProcessingReturn(false);
    }
  };

  const handleExchange = () => {
    setShowReturnPinModal(true);
  };

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      <Header />

      <div className="flex-1 flex overflow-hidden">
        <CustomerPanel
          clearTrigger={clearCustomerTrigger}
          refetchTrigger={refetchTransactionsTrigger}
          selectedCustomer={selectedCustomer}
          onSelectCustomer={handleCustomerSelect}
          // Sales person selection disabled for now — may be revisited in the future.
          // selectedSalesPerson={selectedSalesPerson}
          // onSelectSalesPerson={setSelectedSalesPerson}
          // clearSalesPersonTrigger={clearSalesPersonTrigger}
        />

        <ScanBasketArea
          selectedLineIndex={selectedLineIndex}
          onSelectLine={setSelectedLineIndex}
          basketItems={basketItems}
          setBasketItems={setBasketItems}
          onReturnQtyUpdate={handleReturnQtyUpdate}
          onReturnLineRemove={handleReturnLineRemove}
          isReturnMode={!!returnContext && !returnProcessed}
          onBogoQtyChange={handleBogoQtyChange}
          activeBogoPromos={activeBogoPromos}
        />

        <div className="flex flex-col w-96">
          <TotalsPanel
            basketItems={basketItems}
            orderDiscount={orderDiscount}
            onRemoveDiscount={handleRemoveOrderDiscount}
          />
          <ActionButtons
            selectedLineIndex={selectedLineIndex}
            onTender={handleTender}
            onReturn={handleReturn}
            onSupervisorAction={handleSupervisorAction}
            onPrint={handlePrint}
            onParkSale={handleParkSale}
            onRecall={handleRecall}
            hasParkedSales={hasParkedSales}
            onOrderDiscount={handleOrderDiscount}
            orderDiscountDisabled={!basketItems.length}
            onCancelTransaction={handleCancelTransaction}
            onExchange={handleExchange}
            tenderDisabled={bogoLimitExceeded || !basketItems.length}
            returnMode={!!returnContext && !returnProcessed}
            returnProcessed={returnProcessed}
            returnInProgress={!!returnContext}
            isProcessingReturn={isProcessingReturn}
            onRefund={handleRefundClick}
            onExchangeAction={handleExchangeAction}
            refundAllowed={refundAllowed}
          />
          {returnError && (
            <div className="mx-4 mb-4 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
              {returnError}
            </div>
          )}
        </div>
      </div>

      <StatusBar mode={mode} />

      {showTenderModal && (
        <TenderModal
          onClose={handleCloseTender}
          onPaymentComplete={handlePaymentComplete}
          grandTotal={grandTotal}
          customerId={resolvedCustomerId}
          exchangeCreditMemo={exchangeCreditMemo}
        />
      )}

      {showReturnPinModal && (
        <SupervisorPinModal
          title="Supervisor Authorization"
          subtitle="Enter PIN to access return / exchange"
          onSuccess={() => { setShowReturnPinModal(false); setShowExchangeModal(true); }}
          onClose={() => setShowReturnPinModal(false)}
        />
      )}

      {showCancelPinModal && (
        <SupervisorPinModal
          title="Supervisor Authorization"
          subtitle="Enter PIN to cancel this transaction"
          onSuccess={() => { setShowCancelPinModal(false); doCancelTransaction(); }}
          onClose={() => setShowCancelPinModal(false)}
        />
      )}

      {showReturnModal && <ReturnModal onClose={handleCloseReturn} />}

      {showSupervisorModal && (
        <SupervisorModal
          action={supervisorAction}
          onClose={handleCloseSupervisor}
        />
      )}

      {showPrintModal && (
        <PrintReceiptModal
          onClose={handleClosePrint}
          paymentMethods={receiptSnapshot?.paymentMethods ?? paymentMethods}
          actualPayments={receiptSnapshot?.actualPayments}
          basketItems={receiptSnapshot?.basketItems ?? []}
          subtotal={receiptSnapshot?.subtotal ?? 0}
          discount={receiptSnapshot?.discount ?? 0}
          orderDiscount={receiptSnapshot?.orderDiscount ?? 0}
          tax={receiptSnapshot?.tax ?? 0}
          total={receiptSnapshot?.total ?? 0}
          receiptData={receiptSnapshot?.receiptData}
          appliedCreditMemo={receiptSnapshot?.appliedCreditMemo}
        />
      )}

      {showReturnReceiptModal && returnReceiptSnapshot && (
        <ReturnReceiptModal
          onClose={handleCloseReturnReceipt}
          basketItems={returnReceiptSnapshot.basketItems}
          subtotal={returnReceiptSnapshot.subtotal}
          discount={returnReceiptSnapshot.discount}
          tax={returnReceiptSnapshot.tax}
          total={returnReceiptSnapshot.total}
          isRefund={returnReceiptSnapshot.isRefund}
          receiptData={returnReceiptSnapshot.receiptData}
          creditMemoAmount={returnReceiptSnapshot.receiptData?.creditMemoAmount}
        />
      )}

      {showParkSaleModal && (
        <ParkSaleModal
          onClose={handleCloseParkSale}
          basketItems={basketItems}
          selectedCustomer={selectedCustomer}
          onParkSuccess={handleParkSuccess}
          onRetrieveSale={handleRetrieveSale}
          initialTab={parkSaleInitialTab}
          switchToRetrieveAfterPark={parkSaleAndRetrieve}
        />
      )}

      {showRecallConflict && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-alert-line text-yellow-600 text-xl"></i>
              </div>
              <h2 className="text-lg font-bold text-gray-900">
                Active Sale in Progress
              </h2>
            </div>
            <p className="text-sm text-gray-600 mb-5">
              There are items in the current sale. What would you like to do
              before retrieving?
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={handleConflictParkAndRetrieve}
                className="w-full px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold transition-colors cursor-pointer text-sm"
              >
                <i className="ri-save-line mr-2"></i>
                Park & Retrieve
              </button>
              <button
                onClick={handleConflictClearAndRetrieve}
                className="w-full px-4 py-3 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-lg font-semibold transition-colors cursor-pointer text-sm"
              >
                <i className="ri-delete-bin-line mr-2"></i>
                Clear & Retrieve
              </button>
              <button
                onClick={() => setShowRecallConflict(false)}
                className="w-full px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-semibold transition-colors cursor-pointer text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showOrderDiscountModal && (
        <OrderDiscountModal
          onClose={handleCloseOrderDiscount}
          onApply={handleApplyOrderDiscount}
          onRequireApproval={handleRequireOrderDiscountApproval}
          discountAccess={orderDiscountAccess}
          currentDiscount={orderDiscount}
          amountBeforeDiscount={subtotal}
          lineItemDiscount={itemDiscounts}
          taxBeforeOrderDiscount={taxBeforeOrderDiscount}
          basketItems={basketItems}
          customerId={resolvedCustomerId}
        />
      )}

      {showOrderDiscountApprovalModal && (
        <SupervisorPinModal
          title="Supervisor Authorization"
          subtitle={orderDiscountApprovalReason || "Enter PIN to apply order discount"}
          onSuccess={() => {
            setShowOrderDiscountApprovalModal(false);
            if (pendingOrderDiscount) {
              // Approval for a discount value already entered (over the applicable limit) — apply and close.
              handleApplyOrderDiscount(pendingOrderDiscount);
              setPendingOrderDiscount(null);
              setShowOrderDiscountModal(false);
            } else {
              // Approval for the initial "no permission" gate — now let them enter a discount value.
              setOrderDiscountAccess({ allowDiscount: true, applicableDiscount: 100 });
              setShowOrderDiscountModal(true);
            }
          }}
          onClose={() => setShowOrderDiscountApprovalModal(false)}
        />
      )}

      {showExchangeModal && (
        <ExchangeModal
          onClose={() => setShowExchangeModal(false)}
          onInvoiceSelected={handleReturnInvoiceSelected}
        />
      )}

      {showRefundConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 text-red-600 rounded-full flex-shrink-0">
                <i className="ri-refund-2-line text-2xl"></i>
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900 whitespace-nowrap">
                  Confirm Refund
                </h2>
                <p className="text-sm text-gray-500 whitespace-nowrap">
                  This action cannot be undone
                </p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-600 whitespace-nowrap">
                Refund amount (Cash)
              </p>
              <p className="text-2xl font-bold text-gray-900 whitespace-nowrap">
                {grandTotal.toFixed(2)}
              </p>
            </div>
            {returnError && (
              <p className="mb-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                {returnError}
              </p>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRefundConfirm(false);
                  setReturnError(null);
                }}
                disabled={isProcessingReturn}
                className="flex-1 px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold text-sm cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRefund}
                disabled={isProcessingReturn}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold text-sm cursor-pointer disabled:opacity-50"
              >
                {isProcessingReturn ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-1"></i>
                    Processing...
                  </>
                ) : (
                  "Confirm Refund"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
